import { useEffect, useMemo } from "react";

import { useAuth } from "@/hooks/useAuth";
import { capture } from "@/lib/sentry/capture";
import { logger } from "@/utils/logger";
import { disciplinesOf, responsibleUserIds } from "./contactsList";
import { useProjectContacts } from "./useProjectContacts";
import { ContactReadDeniedError, ContactsUnavailableError } from "./types";

export interface ProjectResponsibilities {
  /** The viewer's own responsibilities (discipline ids) in the project. */
  myDisciplineIds: number[];
  /** Users responsible for any of `disciplineIds`, most overlapping first. */
  responsibleFor: (disciplineIds: readonly number[]) => number[];
}

const EMPTY: number[] = [];

/**
 * Who is responsible for which design disciplines in one project, read from
 * the Contacts page's data. Other pages use it to point at people (suggested
 * assignees) or at the viewer's own work ("My disciplines").
 *
 * Only a hint: a viewer without VIEW_CONTACTS, a project not yet chosen, or a
 * failed read all give empty answers and the host page carries on unchanged.
 */
export function useProjectResponsibilities(
  buildingId: number | null,
  projectId: number | null
): ProjectResponsibilities {
  const { userProfile } = useAuth();
  const { data: rows, error, allowed } = useProjectContacts(buildingId, projectId);

  useEffect(() => {
    // Before the migration applies, or when RLS disagrees with the cached
    // permission, there is nothing to report: the hint is simply absent.
    if (
      !error ||
      error instanceof ContactsUnavailableError ||
      error instanceof ContactReadDeniedError
    ) {
      return;
    }
    const err = error instanceof Error ? error : new Error(String(error));
    const sentryEventId = capture(err, {
      tags: { feature: "contacts", action: "load-responsibilities" },
      context: { contacts: { building_id: buildingId, project_id: projectId } },
      fingerprint: ["contacts", "load-responsibilities"],
    });
    void logger.error("ui", "contacts.load-responsibilities", err.message, err, {
      building_id: buildingId,
      project_id: projectId,
      sentry_event_id: sentryEventId,
    });
  }, [error, buildingId, projectId]);

  const visible = allowed && rows ? rows : null;
  const me = userProfile?.id ?? null;
  return useMemo(
    () => ({
      myDisciplineIds: visible && me != null ? disciplinesOf(visible, me) : EMPTY,
      responsibleFor: (disciplineIds) =>
        visible ? responsibleUserIds(visible, disciplineIds) : EMPTY,
    }),
    [visible, me]
  );
}
